import type React from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { Tabs, TabList, Tab, TabPanel, NavItemBadge } from "@similarweb/ui-components";
import type { INavItemBadgeType } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { openStateUnlockModal } from "app/services/ModalService";
import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";

import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { ItemContainer } from "./styledComponent";
import { WebAnalysisGeographyContainer } from "../../geography/WebAnalysisGeographyContainer";
import { DemographicsUserBased } from "../../demographicsUserBased/DemographicsUserBased";
import AudienceInterests from "../../audience-interests/single/AudienceInterests/AudienceInterests";
import { AudienceOverlap } from "../../audience-overlap/AudienceOverlap";

export enum ECategoryAudienceType {
    Geography = "geography",
    Demographics = "demographicsUsersBased",
    AudienceInterests = "audienceInterests",
    AudienceOverlap = "overlap",
}

interface TabConfig {
    id: ECategoryAudienceType;
    titleKey: string;
    Component: React.ComponentType;
    stateName: string;
    isLocked?: boolean;
    isHidden?: boolean;
    hook?: { name: string };
    badge?: INavItemBadgeType;
}

const getTabConfig = (): TabConfig[] => [
    {
        id: ECategoryAudienceType.Geography,
        titleKey: "analysis.audience.geo.title",
        Component: WebAnalysisGeographyContainer,
        stateName: "digitalsuite_website_geography",
        isLocked: isLocked(swSettings?.components?.WebGeography),
        isHidden: isHidden(swSettings?.components?.WebGeography),
        hook: { name: "digitalsuite_website_geography" },
    },
    {
        id: ECategoryAudienceType.Demographics,
        titleKey: "analysis.audience.demo.title",
        Component: DemographicsUserBased,
        stateName: "digitalsuite_website_demographics",
        isLocked: isLocked(swSettings?.components?.WebDemographics),
        isHidden: isHidden(swSettings?.components?.WebDemographics),
        hook: { name: "digitalsuite_website_demographics" },
    },
    {
        id: ECategoryAudienceType.AudienceInterests,
        titleKey: "analysis.audience.interests.title",
        Component: AudienceInterests,
        stateName: "digitalsuite_website_audienceInterests",
        isLocked: isLocked(swSettings?.components?.WebAudienceInterests),
        isHidden: isHidden(swSettings?.components?.WebAudienceInterests),
        hook: { name: "digitalsuite_website_audienceInterests" },
    },
    {
        id: ECategoryAudienceType.AudienceOverlap,
        titleKey: "analysis.audience.overlap.title",
        Component: AudienceOverlap,
        stateName: "digitalsuite_website_audienceOverlap",
        isLocked: isLocked(swSettings?.components?.WebAudienceOverlap),
        isHidden: isHidden(swSettings?.components?.WebAudienceOverlap),
        hook: { name: "digitalsuite_website_audienceOverlap" },
    },
];

export const AudienceWrapper: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const selectedTab = params.selectedTab as ECategoryAudienceType | undefined;
    const defaultTab = ECategoryAudienceType.Geography;

    const visibleTabs = useMemo(() => getTabConfig().filter((tab) => !tab.isHidden), []);

    const activeTabId = selectedTab ?? defaultTab;

    const activeTabIndex = useMemo(() => {
        const index = visibleTabs.findIndex((tab) => tab.id === activeTabId);
        return index >= 0 ? index : 0;
    }, [activeTabId, visibleTabs]);

    const handleTabSelect = (index: number) => {
        const { id, isLocked, hook } = visibleTabs[index];
        allTrackers.dispatchTrackEvent("Button", "click", `Audience Tab/${id}`);
        if (isLocked) {
            openStateUnlockModal(hook);
        } else {
            proNavigator.go("digitalsuite_website_audience", {
                ...params,
                selectedTab: id,
                audienceCategory: "",
                orderBy: null,
            });
        }
    };

    return (
        <Tabs
            selectedIndex={activeTabIndex}
            onSelect={handleTabSelect}
        >
            <FlexColumn gap="24px">
                <TabList>
                    {visibleTabs.map(({ id, titleKey, isLocked, badge }) => (
                        <Tab key={id}>
                            <ItemContainer>
                                <span>{translate(titleKey)}</span>
                                {isLocked ? (
                                    <SWReactIcons
                                        iconName="spark"
                                        size="xs"
                                    />
                                ) : null}
                                {badge ? <NavItemBadge type={badge} /> : null}
                            </ItemContainer>
                        </Tab>
                    ))}
                </TabList>
                {visibleTabs.map(({ Component }, index) => (
                    <TabPanel key={index}>{index === activeTabIndex ? <Component /> : null}</TabPanel>
                ))}
            </FlexColumn>
        </Tabs>
    );
};
